import { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MenuOutlined, CloseOutlined } from '@ant-design/icons';

const navItems = [
  { path: '/', label: '首页' },
  { path: '/archive', label: '档案' },
  { path: '/map', label: '地图' },
  { path: '/3d', label: '展厅' },
  { path: '/culture', label: '文化馆' },
  { path: '/community', label: '共创' },
  { path: '/mall', label: '商城' },
];

export default function TransparentNav({ solid = false }: { solid?: boolean }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [hovered, setHovered] = useState<string | null>(null);
  const [adminHover, setAdminHover] = useState(false);
  const [menuHover, setMenuHover] = useState<string | null>(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // 初始计算
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // solid：子页面常驻浅色毛玻璃（深色文字），不随滚动切换；首页顶部保持透明白字
  const isSolid = solid || scrolled;

  const isActive = useCallback((path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  const go = useCallback((path: string) => {
    setMobileOpen(false);
    if (location.pathname === path) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      navigate(path);
    }
  }, [location.pathname, navigate]);

  const linkColor = isSolid ? '#3B2A26' : '#fff';

  return (
    <>
      <nav
        className="xc-nav"
        style={{
          position: 'fixed',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1001,
          maxWidth: 1280,
          width: 'calc(100% - 48px)',
          padding: '14px 32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
          borderRadius: scrolled ? 20 : 16,
          // 背景仅随滚动变化：未滚动保持透明；文字颜色随页面深浅切换（isSolid）
          background: scrolled ? 'rgba(250, 247, 242, 0.88)' : 'transparent',
          backdropFilter: scrolled ? 'blur(16px) saturate(1.2)' : 'none',
          WebkitBackdropFilter: scrolled ? 'blur(16px) saturate(1.2)' : 'none',
          border: scrolled ? '1px solid rgba(163, 35, 43, 0.12)' : '1px solid transparent',
          boxShadow: scrolled ? '0 4px 24px rgba(0,0,0,0.06)' : 'none',
        }}
      >
        {/* Logo */}
        <div
          onClick={() => go('/')}
          style={{
            fontSize: 20,
            fontWeight: 800,
            color: linkColor,
            letterSpacing: '-0.02em',
            cursor: 'pointer',
            userSelect: 'none',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'color 0.3s ease',
            textShadow: isSolid ? 'none' : '0 2px 12px rgba(0,0,0,0.2)',
          }}
        >
          <img
            src="/assets/logo.png"
            alt="湘村新台"
            style={{
              height: 36,
              width: 'auto',
              objectFit: 'contain',
              borderRadius: 8,
              background: isSolid ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.12)',
              padding: 3,
            }}
          />
          <span>湘村新台</span>
        </div>

        {/* Desktop Nav */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 22,
          }}
          className="nav-desktop"
        >
          {navItems.map((item) => {
            const hov = hovered === item.path;
            return (
              <button
                key={item.path}
                onClick={() => go(item.path)}
                onMouseEnter={() => setHovered(item.path)}
                onMouseLeave={() => setHovered(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: 14,
                  whiteSpace: 'nowrap',
                  fontWeight: 500,
                  color: hov
                    ? (isSolid ? '#D4A017' : '#FFD97A')
                    : isActive(item.path) ? (isSolid ? '#A3232B' : '#FFD97A') : linkColor,
                  cursor: 'pointer',
                  padding: '4px 0',
                  borderBottom: hov
                    ? `2px solid ${isSolid ? '#D4A017' : '#FFD97A'}`
                    : isActive(item.path) ? `2px solid ${isSolid ? '#A3232B' : '#FFD97A'}` : '2px solid transparent',
                  transform: hov ? 'translateY(-2px)' : 'translateY(0)',
                  transition: 'all 0.3s ease',
                  fontFamily: 'inherit',
                  textShadow: isSolid ? 'none' : '0 2px 12px rgba(0,0,0,0.2)',
                }}
              >
                {item.label}
              </button>
            );
          })}
          <button
            onClick={() => go('/admin/dashboard')}
            onMouseEnter={() => setAdminHover(true)}
            onMouseLeave={() => setAdminHover(false)}
            style={{
              padding: '6px 16px',
              borderRadius: 20,
              border: adminHover
                ? '1px solid #A3232B'
                : isSolid ? '1px solid rgba(163, 35, 43, 0.3)' : '1px solid rgba(255,255,255,0.45)',
              background: adminHover
                ? '#A3232B'
                : isSolid ? 'transparent' : 'rgba(255,255,255,0.14)',
              color: adminHover ? '#FAF7F2' : isSolid ? '#A3232B' : '#fff',
              fontSize: 13,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transform: adminHover ? 'translateY(-2px)' : 'translateY(0)',
              boxShadow: adminHover ? '0 6px 16px rgba(163,35,43,0.35)' : 'none',
              transition: 'all 0.3s ease',
            }}
          >
            后台管理
          </button>
        </div>

        {/* Mobile Hamburger */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          style={{
            display: 'none',
            background: 'none',
            border: 'none',
            fontSize: 20,
            color: linkColor,
            cursor: 'pointer',
            textShadow: isSolid ? 'none' : '0 2px 12px rgba(0,0,0,0.2)',
          }}
          className="nav-mobile-btn"
        >
          {mobileOpen ? <CloseOutlined /> : <MenuOutlined />}
        </button>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileOpen && (
        <div
          className="xc-menu-overlay"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1000,
            background: 'rgba(250, 247, 242, 0.96)',
            backdropFilter: 'blur(20px)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 28,
          }}
        >
          {navItems.map((item) => {
            const mh = menuHover === item.path;
            return (
              <button
                key={item.path}
                onClick={() => go(item.path)}
                onMouseEnter={() => setMenuHover(item.path)}
                onMouseLeave={() => setMenuHover(null)}
                style={{
                  background: mh ? 'rgba(163, 35, 43, 0.08)' : 'transparent',
                  border: 'none',
                  fontSize: 24,
                  fontWeight: 600,
                  color: mh ? '#A3232B' : isActive(item.path) ? '#A3232B' : '#3B2A26',
                  cursor: 'pointer',
                  fontFamily: 'inherit',
                  borderLeft: isActive(item.path) ? '3px solid #A3232B' : '3px solid transparent',
                  padding: '4px 16px',
                  borderRadius: 8,
                  transition: 'all 0.3s ease',
                }}
              >
                {item.label}
              </button>
            );
          })}
          <button
            onClick={() => go('/admin/dashboard')}
            onMouseEnter={() => setMenuHover('__admin')}
            onMouseLeave={() => setMenuHover(null)}
            style={{
              marginTop: 12,
              padding: '10px 28px',
              borderRadius: 24,
              border: menuHover === '__admin'
                ? '1px solid #A3232B'
                : '1px solid rgba(163, 35, 43, 0.3)',
              background: menuHover === '__admin' ? '#A3232B' : 'transparent',
              color: menuHover === '__admin' ? '#FAF7F2' : '#A3232B',
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'inherit',
              transition: 'all 0.3s ease',
            }}
          >
            后台管理
          </button>
        </div>
      )}

      <style>{`
        .nav-mobile-btn { transition: transform 0.25s ease, color 0.3s ease; }
        .nav-mobile-btn:hover { transform: scale(1.12); }
        .nav-mobile-btn:active { transform: scale(0.92); }
        @media (max-width: 1024px) {
          .nav-desktop { display: none !important; }
          .nav-mobile-btn { display: block !important; }
          nav { top: 8px !important; padding: 12px 24px !important; width: calc(100% - 48px) !important; }
        }
      `}</style>
    </>
  );
}
